// COPIED from extensions/carrier-marks by scripts/sync-carrier-marks.mjs.
// Do not edit here — edit the extension, then run `npm run sync-carrier-marks`.
// Pure decision logic for the carrier-marks checkout extension. No Shopify
// imports here so it can be unit-tested with plain node (`npm test`).
//
// Contract with Shippy (backend/shippy/shopify_carrier_service.py,
// apply_carrier_marks): for the countries in PackingConfig.CARRIER_MARK_COUNTRIES
// a rate's service_code carries
//   <base>.<carrier>.<parcels>.<pallets>     e.g. shippy_standard.gls.2.0
// and its description is sent EMPTY, so the extension draws
//   [mark] Shipped as 2 parcels, Protected & Insured
// as the option's second line, in the buyer's checkout language (the sentence
// itself is built by sentence.js from the extension's locale files; Shopify
// never translates a carrier-service description, this line is the one place
// it can be localised).
//
// WHERE IT DRAWS (single source of truth = the backend set): a code that
// carries those details is drawn wherever it comes from. Adding a country
// means adding it to CARRIER_MARK_COUNTRIES and deploying Shippy; nothing to
// change in Shopify. The optional "Countries" block setting only adds
// countries where the extension draws the MARK ALONE under Shopify's own
// description (preview before the backend switch). It cannot switch a
// backend-marked country off; removing the block in the checkout editor does.
//
// Rates that are not Shippy's (Shopify manual rates in BE/NL, backup rates,
// draft orders) have no `shippy_` code and get nothing at all.

export const CARRIERS = ['gls', 'dhl', 'ups', 'bpost', 'geodis', 'chronopost'];

export const CARRIER_LABEL = {
  gls: 'GLS', dhl: 'DHL', ups: 'UPS', bpost: 'bpost', geodis: 'Geodis', chronopost: 'Chronopost',
};

export function parseCountries(setting) {
  return new Set(
    String(setting || '')
      .split(/[,\s;]+/)
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s.length === 2),
  );
}

/**
 * @param {{code?: string, description?: string, countryCode?: string, countries?: string|Set<string>}} input
 * @returns {{mark: string, carrier: string|null, pickup: boolean, sentence: object|null} | null}
 *   sentence: {kind: 'protected'} | {kind: 'shipped', parcels, pallets}
 *             | {kind: 'pickup', parcels} | {kind: 'locker', parcels} | null
 */
export function resolveMark({ code, description, countryCode, countries }) {
  const raw = String(code || '');
  if (!raw.startsWith('shippy_')) return null;

  const hasDetails = raw.includes('.');
  if (!hasDetails) {
    // Plain Shippy code: Shopify draws the description itself. Draw the mark
    // alone only where the block setting asks for it.
    const allowed = countries instanceof Set ? countries : parseCountries(countries);
    if (!countryCode || !allowed.has(String(countryCode).toUpperCase())) return null;
  }

  const [base, carrierKey = '-', parcelsRaw = '', palletsRaw = ''] = raw.split('.');
  const parcels = parseInt(parcelsRaw, 10) || 0;
  const pallets = parseInt(palletsRaw, 10) || 0;
  const carrier = CARRIERS.includes(carrierKey) ? carrierKey : null;

  let mark;
  let shown = null; // carrier the mark stands for (accessibility label)
  let pickup = false;
  if (base === 'shippy_free') {
    mark = 'delivery';
  } else if (base === 'shippy_pickup' || base === 'shippy_locker') {
    // The pickup network's own logo (GLS), the shop icon only as fallback
    // (Geoffrey, 2026-09-10).
    mark = carrier || 'pickup';
    pickup = true;
  } else if (base === 'shippy_partial') {
    // Split Shipment: mark + a short translated sentence, so the row is two
    // lines tall like its neighbours (Geoffrey, 2026-09-10).
    mark = 'split';
  } else if (base === 'shippy_self_ship') {
    return null;
  } else if (/^shippy_carrier_\d+$/.test(base)) {
    // Alternative rates already name the carrier in their title: show its mark.
    mark = carrier || 'delivery';
    shown = carrier;
  } else if (base === 'shippy_standard') {
    // Pilot decision (Geoffrey, 2026-09-10): Standard / Oversized stay
    // generic, not linked to a carrier. The code still carries the carrier
    // key, so flipping this to a carrier mark is a one-line change here.
    mark = pallets > 0 ? 'pallet' : 'delivery';
  } else {
    return null;
  }

  // Sentence: only when Shippy moved the description into the code AND
  // Shopify shows no description of its own (never duplicate a line).
  let sentence = null;
  if (hasDetails && !(description || '').trim()) {
    if (base === 'shippy_free') sentence = { kind: 'protected' };
    else if (base === 'shippy_partial') sentence = { kind: 'split' };
    else if (base === 'shippy_pickup') sentence = { kind: 'pickup', parcels };
    else if (base === 'shippy_locker') sentence = { kind: 'locker', parcels };
    else sentence = (parcels > 0 || pallets > 0) ? { kind: 'shipped', parcels, pallets } : { kind: 'protected' };
  }
  return { mark, carrier: shown, pickup, sentence };
}
